/**
 * Per-user persistence for one-time onboarding tours.
 *
 * Tour "seen" state has historically lived only in localStorage, which is
 * per-browser and gets wiped in Incognito / on a new device — so a tour would
 * replay on every fresh session even though the user has already seen it.
 *
 * We persist the seen-state per user in the server-side user settings
 * (UserSettings.preferences.tours_seen) and seed localStorage from it on login.
 * That makes each tour show only once per user across browsers, devices and
 * Incognito sessions. localStorage stays the synchronous source of truth the
 * tour hooks read; the server is the durable backing store that survives
 * logout / localStorage.clear().
 */
import { settingsApi } from '../services/settingsApi'
import { logger } from './logger'

const TOURS_PREF_KEY = 'tours_seen'

/**
 * Tour localStorage keys that are persisted per-user on the server.
 * Keep in sync with the *_STORAGE_KEY constants in the tour modules.
 */
export const PERSISTED_TOUR_KEYS = [
  'tagp_agent_studio_canvas_tour_v1',
  'tagp_agent_studio_list_tour_v1',
  'tagp_workflows_tour_v1',
] as const

function readSeenBlock(prefs: Record<string, unknown> | null | undefined): Record<string, boolean> {
  const raw = (prefs ?? {})[TOURS_PREF_KEY]
  return raw && typeof raw === 'object' ? { ...(raw as Record<string, boolean>) } : {}
}

/**
 * Pull per-user tour-seen state from the server and seed localStorage.
 * Call once the user is authenticated (before they reach a tour-bearing page).
 * Non-fatal: on failure we silently fall back to localStorage-only behaviour.
 */
export async function syncToursFromServer(): Promise<void> {
  try {
    const settings = await settingsApi.getSettings()
    const seen = readSeenBlock(settings.preferences)
    for (const key of PERSISTED_TOUR_KEYS) {
      if (seen[key]) {
        try {
          localStorage.setItem(key, '1')
        } catch {
          /* ignore storage errors */
        }
      }
    }
  } catch (err) {
    logger.warn('Failed to sync tour state from server', err)
  }
}

/**
 * Mark a tour as seen for the current user — synchronously in localStorage and
 * durably on the server. Safe to call repeatedly; the server write no-ops once
 * the flag is already persisted.
 */
export function markTourSeen(key: string): void {
  try {
    localStorage.setItem(key, '1')
  } catch {
    /* ignore storage errors */
  }
  if ((PERSISTED_TOUR_KEYS as readonly string[]).includes(key)) {
    void persistTourSeenToServer(key)
  }
}

async function persistTourSeenToServer(key: string): Promise<void> {
  try {
    // Re-fetch so we merge into the latest preferences and don't clobber other
    // preference blocks (e.g. ai_workflows) — PUT /settings replaces the whole
    // preferences object.
    const settings = await settingsApi.getSettings()
    const seen = readSeenBlock(settings.preferences)
    if (seen[key]) return
    seen[key] = true
    const nextPrefs = { ...(settings.preferences ?? {}), [TOURS_PREF_KEY]: seen }
    await settingsApi.updateSettings({ preferences: nextPrefs })
  } catch (err) {
    logger.warn('Failed to persist tour seen state to server', err)
  }
}
